import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FolderVisibility = 'private' | 'public_org' | 'public' | 'custom';
export type FolderParentType = 'Project' | 'Feature' | 'Folder';

export interface Folder {
  _id: string;
  orgId?: string;
  name: string;
  description?: string;
  parentId?: string;
  parentType?: FolderParentType;
  projectId: string;
  projectName?: string;
  hierarchyPath: string[];
  hierarchyPathNames: string[];
  depth: number;
  startDate?: string;
  estimatedEndDate?: string;
  visibility: FolderVisibility;
  inheritVisibility: boolean;
  stats: {
    totalChildren: number;
    lastActivityAt?: string;
  };
  createdBy?: string;
  createdByName?: string;
  deletedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FolderChildren {
  folders: Folder[];
  tasks: any[];
  features: any[];
}

interface FolderState {
  folders: Folder[];
  currentFolder: Folder | null;
  children: FolderChildren | null;
  loading: boolean;
  error: string | null;
}

const initialState: FolderState = {
  folders: [],
  currentFolder: null,
  children: null,
  loading: false,
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchFoldersByProject = createAsyncThunk(
  'folders/fetchByProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/folders/project/${projectId}`);
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch folders');
    }
  }
);

export const fetchFolderById = createAsyncThunk(
  'folders/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/folders/${id}`);
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch folder');
    }
  }
);

export const fetchFolderChildren = createAsyncThunk(
  'folders/fetchChildren',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/folders/${id}/children`);
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch folder children');
    }
  }
);

export const createFolder = createAsyncThunk(
  'folders/create',
  async (
    data: {
      projectId: string;
      name: string;
      description?: string;
      parentId?: string;
      parentType?: FolderParentType;
      visibility?: FolderVisibility;
      startDate?: string;
      estimatedEndDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post('/folders', data);
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create folder');
    }
  }
);

export const updateFolder = createAsyncThunk(
  'folders/update',
  async (
    {
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        description?: string;
        visibility?: FolderVisibility;
        startDate?: string;
        estimatedEndDate?: string;
        amendmentReason?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put(`/folders/${id}`, data);
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/folders/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete folder');
    }
  }
);

export const moveFolder = createAsyncThunk(
  'folders/move',
  async (
    { id, newParentId, newParentType }: { id: string; newParentId: string; newParentType: FolderParentType },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(`/folders/${id}/move`, { newParentId, newParentType });
      return res.data.data ?? res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to move folder');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const updateFolderInState = (state: FolderState, updated: Folder) => {
  const idx = state.folders.findIndex(f => f._id === updated._id);
  if (idx !== -1) state.folders[idx] = updated;
  if (state.currentFolder?._id === updated._id) state.currentFolder = updated;
};

const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    clearFolderError: (state) => { state.error = null; },
    clearCurrentFolder: (state) => {
      state.currentFolder = null;
      state.children = null;
    },
    clearFolders: (state) => {
      state.folders = [];
      state.currentFolder = null;
      state.children = null;
    },
  },
  extraReducers: (builder) => {
    const pending = (state: FolderState) => { state.loading = true; state.error = null; };
    const rejected = (state: FolderState, action: any) => {
      state.loading = false;
      state.error = action.payload as string;
    };

    builder
      .addCase(fetchFoldersByProject.pending, pending)
      .addCase(fetchFoldersByProject.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = action.payload;
      })
      .addCase(fetchFoldersByProject.rejected, rejected)

      .addCase(fetchFolderById.pending, pending)
      .addCase(fetchFolderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFolder = action.payload;
      })
      .addCase(fetchFolderById.rejected, rejected)

      .addCase(fetchFolderChildren.pending, pending)
      .addCase(fetchFolderChildren.fulfilled, (state, action) => {
        state.loading = false;
        state.children = action.payload;
      })
      .addCase(fetchFolderChildren.rejected, rejected)

      .addCase(createFolder.pending, pending)
      .addCase(createFolder.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.folders.some(f => f._id === action.payload._id)) {
          state.folders.push(action.payload);
        }
      })
      .addCase(createFolder.rejected, rejected)

      .addCase(updateFolder.pending, pending)
      .addCase(updateFolder.fulfilled, (state, action) => {
        state.loading = false;
        updateFolderInState(state, action.payload);
      })
      .addCase(updateFolder.rejected, rejected)

      .addCase(deleteFolder.pending, pending)
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = state.folders.filter(f => f._id !== action.payload);
        if (state.currentFolder?._id === action.payload) state.currentFolder = null;
      })
      .addCase(deleteFolder.rejected, rejected)

      .addCase(moveFolder.pending, pending)
      .addCase(moveFolder.fulfilled, (state, action) => {
        state.loading = false;
        updateFolderInState(state, action.payload);
      })
      .addCase(moveFolder.rejected, rejected);
  },
});

export const { clearFolderError, clearCurrentFolder, clearFolders } = folderSlice.actions;
export default folderSlice.reducer;
